import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { ErrorNotice } from './Layout';

interface Item {
  id: string;
  name: string;
  color: string;
}
type Kind = 'categories' | 'agencies' | 'labels';
const config: Record<Kind, { title: string; description: string; color: string }> = {
  categories: {
    title: 'ประเภทเอกสาร',
    description: 'เช่น หนังสือเวียน คำสั่ง ระเบียบ รายงาน',
    color: '#0f766e',
  },
  agencies: {
    title: 'หน่วยงานเจ้าของเอกสาร',
    description: 'เช่น กรมบัญชีกลาง กระทรวงมหาดไทย',
    color: '#0369a1',
  },
  labels: { title: 'Label เอกสาร', description: 'คำช่วยค้นที่ใส่ได้หลายค่า', color: '#7c3aed' },
};

export function DocumentTaxonomySettings() {
  return (
    <div className="card space-y-5">
      <div>
        <h2 className="font-semibold">ตั้งค่าคลังเอกสาร</h2>
        <p className="text-sm text-slate-500">สร้าง แก้ไข ลบ และกำหนดสีได้จากที่เดียว</p>
      </div>
      {(Object.keys(config) as Kind[]).map((kind) => (
        <TaxonomySection key={kind} kind={kind} />
      ))}
    </div>
  );
}

function TaxonomySection({ kind }: { kind: Kind }) {
  const client = useQueryClient();
  const [name, setName] = useState('');
  const [color, setColor] = useState(config[kind].color);
  const [editing, setEditing] = useState<string | null>(null);
  const query = useQuery({
    queryKey: ['document-taxonomy', kind],
    queryFn: () => api<{ data: Item[] }>(`/api/document-taxonomy/${kind}`).then((r) => r.data),
  });
  const save = useMutation({
    mutationFn: () =>
      api(
        editing ? `/api/document-taxonomy/${kind}/${editing}` : `/api/document-taxonomy/${kind}`,
        { method: editing ? 'PATCH' : 'POST', body: JSON.stringify({ name, color }) },
      ),
    onSuccess: async () => {
      setName('');
      setColor(config[kind].color);
      setEditing(null);
      await Promise.all([
        client.invalidateQueries({ queryKey: ['document-taxonomy', kind] }),
        client.invalidateQueries({ queryKey: ['color-groups'] }),
      ]);
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => api(`/api/document-taxonomy/${kind}/${id}`, { method: 'DELETE' }),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({ queryKey: ['document-taxonomy', kind] }),
        client.invalidateQueries({ queryKey: ['color-groups'] }),
      ]),
  });
  const error = save.error ?? remove.error ?? query.error;
  return (
    <section className="rounded-xl border p-3">
      <div>
        <h3 className="font-semibold">{config[kind].title}</h3>
        <p className="text-xs text-slate-500">{config[kind].description}</p>
      </div>
      <div className="mt-2 space-y-2">
        {query.data?.map((item) => (
          <div key={item.id} className="flex items-center gap-2">
            <span className="h-4 w-4 rounded-full" style={{ backgroundColor: item.color }} />
            <span className="min-w-0 flex-1 truncate text-sm">{item.name}</span>
            <button
              className="text-sm font-semibold text-teal-700"
              onClick={() => {
                setEditing(item.id);
                setName(item.name);
                setColor(item.color);
              }}
            >
              แก้ไข
            </button>
            <button
              className="text-sm font-semibold text-red-700"
              onClick={() => {
                if (confirm(`ลบ ${item.name}?`)) remove.mutate(item.id);
              }}
            >
              ลบ
            </button>
          </div>
        ))}
      </div>
      <div className="mt-3 grid grid-cols-[1fr_44px_auto] gap-2">
        <input
          className="field"
          placeholder={`ชื่อ${config[kind].title}`}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <input
          className="h-11 w-11 rounded border p-1"
          type="color"
          value={color}
          onChange={(e) => setColor(e.target.value)}
        />
        <button
          className="btn-primary"
          disabled={!name.trim() || save.isPending}
          onClick={() => save.mutate()}
        >
          {editing ? 'บันทึก' : '+ เพิ่ม'}
        </button>
      </div>
      {editing && (
        <button
          className="mt-2 text-xs text-slate-600"
          onClick={() => {
            setEditing(null);
            setName('');
            setColor(config[kind].color);
          }}
        >
          ยกเลิกแก้ไข
        </button>
      )}
      {error && (
        <div className="mt-2">
          <ErrorNotice message={error.message} />
        </div>
      )}
    </section>
  );
}
